/* eslint-disable react/display-name */
import "./style.scss";
import Icon, { IconProps } from "@able/react/dist/Icon";
import { AbleCommonProps } from "../Types";
import {
    useRef,
    useEffect,
    forwardRef,
    MutableRefObject,
    ChangeEvent,
    InputHTMLAttributes,
    ChangeEventHandler,
    useId,
    useMemo,
    ReactElement,
} from "react";
import usePicto, { PictoThemes } from "../util/usePicto";

export type CheckboxPropsType = {
    /** Custom id attribute; autogenerated if null */
    id?: string;
    /** name for checkbox  */
    name?: string;
    /** value for checkbox  */
    value?: string;
    /** Additional CSS classes */
    className?: string;
    /** description for checkbox  */
    description?: string | string[];
    /** Label for checkbox  */
    label?: string;
    /** Image Type - can only be used in Comfortable variant. */
    imageType?: "none" | "Icon" | "Pictogram";
    /** Variant  */
    variant?: "Compact" | "Comfortable";
    /** On Change event */
    onChange: ChangeEventHandler<HTMLInputElement>;
    /** icon to be displayed in Comfortable variant */
    icon?: IconProps;
    /** picto to be displayed in Comfortable variant */
    picto?: any;
    /** Picto color */
    pictoTheme?: PictoThemes;
    /** checked for checkbox */
    checked: boolean;
    /** indeterminate state */
    indeterminate?: boolean;
    /** error state */
    error?: boolean;
    /** disabled state */
    disabled?: boolean;
    /** aria-describedBy for checkbox element*/
    ariaDescribedBy?: string;
    /** error text to show */
    errorText?: string | ReactElement<HTMLSpanElement>;
    /** other html attributes for checkbox element*/
    other?: InputHTMLAttributes<HTMLInputElement>;
    /** required attribute checkbox */
    required?: boolean;
    type?: 'table' | 'default';
    /** required only for table type checkbox */
    ariaLabeledBy?: string;
} & AbleCommonProps;

type ImageTypeProps = {
    pictoTheme?: PictoThemes;
    pictoComponent:any;
    imageType?: "none" | "Icon" | "Pictogram";
    icon?: IconProps;
    picto?: any;
} & AbleCommonProps;

const RenderImageType = ({
    imageType,
    icon,
    developmentUrl,
    pictoComponent,
}: ImageTypeProps) => {
    if (imageType === "Icon" && icon) {
        return (
            <Icon
                className="ted-ui-checkbox--icon"
                icon={icon.icon}
                size={icon.size}
                developmentUrl={developmentUrl}
            />
        );
    } else if (imageType === "Pictogram" && pictoComponent) {
        return pictoComponent;
    } else return null;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxPropsType>(
    (
        {
            id,
            label,
            description,
            name,
            value,
            disabled,
            imageType,
            variant = 'Compact',
            onChange,
            checked,
            indeterminate,
            error,
            icon,
            picto,
            pictoTheme = 'blue',
            className = "",
            other,
            developmentUrl,
            ariaDescribedBy,
            errorText,
            required,
            type = 'default',
            ariaLabeledBy
        },
        ref
    ) => {
        const inputId = id || useId();
        const errorId = `${inputId}-error`;
        const descriptionId = `${inputId}-description`;
        const isComfortable = variant === "Comfortable";
        const pictoComponent = usePicto(picto?.pictoName, pictoTheme);
        const checkboxRef: MutableRefObject<HTMLInputElement | null> = useRef(null);

        const synchronizeRefs = (el: HTMLInputElement | null) => {
            checkboxRef.current = el;
            if (!ref) {
                return;
            } else if (typeof ref === "object") {
                ref.current = el;
            } else {
                ref(el);
            }
        }

        const getAriaDescribedBy = useMemo(() => {
            if (ariaDescribedBy || errorText || description) {
                return {
                    "aria-describedby": `${description ? descriptionId : ""} ${ariaDescribedBy ? ariaDescribedBy : ""} ${error && errorText ? errorId : ""}`,
                };
            }
            return {};
        }, [ariaDescribedBy, errorText, description, descriptionId, errorId, error]);


        useEffect(() => {
            if (checkboxRef.current) {
                checkboxRef.current.indeterminate = !!indeterminate;
                checkboxRef.current.checked = indeterminate ? false : checked;
            }
        }, [indeterminate, checked]);

        const getCustomClass = useMemo(() => {
            return `${error ? '-error' : ''}${disabled ? ' disabled' : ''}`;
        }, [error, disabled]);

        const getVariantClass = useMemo(() => {
            if (isComfortable) {
                if (imageType === "Icon") {
                    return "ted-ui-checkbox-comfortable-withIcon";
                } else if (imageType === "Pictogram") {
                    return "ted-ui-checkbox-comfortable-withPictogram";
                } else {
                    return "ted-ui-checkbox-comfortable";
                }
            } else {
                return "ted-ui-checkbox-compact";
            }
        }, [imageType, isComfortable]);

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            if(disabled) e.preventDefault();
            else if (onChange && typeof onChange === "function") {
                onChange(e);
                if (checkboxRef.current) checkboxRef.current.indeterminate = !!indeterminate;
            }
        };

        const getAriaLabeledBy = useMemo(() => {
            if(type === 'table' && ariaLabeledBy) {
                return {
                    "aria-labelledby" : ariaLabeledBy
                }
            }
            return {}
        }, [ariaLabeledBy]);

        return (
            <div
                className={`ted-ui-checkbox ${getVariantClass}${getCustomClass} ${className} ted-ui-checkbox-type-${type}`}
                data-testid="variant"
            >
                <input
                    {...other}
                    id={inputId}
                    type="checkbox"
                    name={name}
                    value={value}
                    checked={checked}
                    onChange={handleChange}
                    ref={synchronizeRefs}
                    aria-disabled={disabled}
                    data-testid={id}
                    {...getAriaDescribedBy}
                    className="ted-ui-checkbox--input"
                    required={required}
                    {...getAriaLabeledBy as any}
                />
                <div>
                    <label htmlFor={inputId} className="ted-ui-checkbox--label">
                        {" "}
                        {label}
                    </label>
                    {description && <p id={descriptionId}>
                        <>
                            {Array.isArray(description) ? (
                                description.map((desc, index) => {
                                    return (
                                        <span className="ted-ui-checkbox--description" key={index}>
                                            {desc}
                                        </span>
                                    );
                                })
                            ) : (
                                <span className="ted-ui-checkbox--description">{description}</span>
                            )}
                        </>
                    </p>}
                    {error && errorText && (
                        <span id={errorId} className={`ted-ui-checkbox-error-wrapper`}>
                            <Icon
                                icon="Error"
                                screenReaderContent="Error"
                                developmentUrl={developmentUrl}
                                size="24"
                                className="ted-ui-checkbox-error-icon"
                            />
                            <span className="ted-ui-checkbox-error-text">
                                {errorText}
                            </span>
                        </span>
                    )}
                </div>
                {isComfortable &&
                    RenderImageType({
                        icon: icon,
                        imageType: imageType,
                        pictoComponent:pictoComponent,
                        developmentUrl: developmentUrl,
                    })}
            </div>
        );
    }
);

export type TableCheckboxType = AbleCommonProps & Pick<CheckboxPropsType, 'onChange' | 'checked' | 'indeterminate' | 'ariaLabeledBy' | 'ariaDescribedBy'>;

export const TableCheckbox = ({ developmentUrl, checked, onChange, indeterminate, ...rest }: TableCheckboxType) => {
    return <Checkbox type="table" developmentUrl={developmentUrl as string} onChange={onChange} indeterminate={indeterminate} checked={checked} {...rest}/>
}

export default Checkbox;
